import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DataFhirAdapter,
  DataFhirCategorySetting,
  DataFhirModel,
} from '../data.model';
import { DictionariesCollectionService } from '../dictionaries/dictionaries-collection.service';
import { AddressAdapter } from '../partial/address.model';
import { Provider } from './base-provider.model';

export type Organization = DataFhirModel<fhir4.Organization> &
  Provider & {
    mandantIdentifier?: string;
  };

@Injectable({
  providedIn: 'root',
})
export class OrganizationAdapter extends DataFhirAdapter<Organization> {
  categorySettings: DataFhirCategorySetting<Organization>[] = [
    {
      categoryName: 'facilityType',
      dictionaryIds: ['1.2.276.0.76.11.58--20211114161028'],
      resourcePosition: 'type',
    },
    {
      categoryName: 'level',
      dictionaryIds: [],
      resourcePosition: 'type',
      _optionsList: [
        {
          id: 'O0',
          name: 'Organization auf 0. Ebene',
          system: 'urn:oid:1.2.276.0.76.3.1.471.1.100.4',
        },
        {
          id: 'O1',
          name: 'Organization auf 1. Ebene',
          system: 'urn:oid:1.2.276.0.76.3.1.471.1.100.4',
        },
        {
          id: 'O2',
          name: 'Organization auf 2. Ebene',
          system: 'urn:oid:1.2.276.0.76.3.1.471.1.100.4',
        },
      ],
    },
    {
      categoryName: 'specialty',
      dictionaryIds: ['1.2.276.0.76.11.37--20190517134631'],
      resourcePosition: (organization) =>
        organization.extension
          ? organization?.extension
              .filter(
                ({ url }) =>
                  url ==
                  'http://icw-global.com/fhir/StructureDefinition/hpd-organization-specialty', //NOSONAR
              )
              .map(({ valueCodeableConcept }) => valueCodeableConcept)
          : null,
    },
    {
      categoryName: 'partner',
      dictionaryIds: ['taxonomytermpartner'],
      resourcePosition: (organization) =>
        organization.extension
          ? organization?.extension
              .filter(
                ({ url }) =>
                  url ==
                  'http://icw-global.com/fhir/StructureDefinition/hpd-organization-subject-group', //NOSONAR
              )
              .map(({ valueCodeableConcept }) => valueCodeableConcept)
          : null,
    },
    {
      categoryName: 'subjects',
      dictionaryIds: ['taxonomytermsubject'],
    },
  ];

  constructor(
    protected dictionariesCollection: DictionariesCollectionService,
    private addressAdapter: AddressAdapter,
  ) {
    super('Organization', dictionariesCollection);
  }

  fromResource$(organization: fhir4.Organization): Observable<Organization> {
    return forkJoin([
      super.fromResource$(organization),
      this.addressAdapter.fromResource$(organization?.address),
    ]).pipe(
      map(([baseItem, addressDetails]) => {
        const mandantFhirIdentifier = organization.identifier.find(
          (element) => element.type.coding[0].code == 'mandant-pat-ns',
        );
        const mandantIdentifier = mandantFhirIdentifier?.value;

        /** @todo with converting adapters to observable this type of partner should be found in taxonomytermpartner dictionary (by name = system) and stored in _categories */
        const partnerSystem = mandantFhirIdentifier?.system;

        const identifier = organization.identifier.find(
          (element) => element.system == 'urn:ietf:rfc:3986',
        )?.value;
        const name = organization.name;
        const facilityType =
          baseItem?._categories?.['facilityType']?.map(
            ({ name }) => name,
          )?.[0] || '';
        const specialty = baseItem?._categories?.['specialty']?.map(
          ({ name }) => name,
        );
        const address = addressDetails.display;
        const parentOrganizationId =
          organization.partOf && organization.partOf.reference.split('/').pop();

        const providerTypeCode = baseItem._categories['level'][0]?.id;

        /** Define ProviderType according to O0, O1, O2 */
        let providerType;
        if (providerTypeCode == 'O1') {
          providerType = 'facility';
        } else if (providerTypeCode == 'O2') {
          providerType = 'station';
        } else {
          providerType = 'parentFacility';
        }

        const provider: Provider = {
          providerType,
          identifier,
          name,
          facilityType,
          specialty,
          address,
          parentOrganizationId,
          partnerSystem,
        };

        return {
          ...baseItem,
          ...provider,
          mandantIdentifier,
        };
      }),
    );
  }

  protected blankResource(): fhir4.Organization {
    return null;
  }

  toResource(organization: Organization): fhir4.Organization {
    return null;
  }
}
